/**
 * Каталог ролей. Тот же набор кодов засевается в таблицу `Role` (prisma/seed),
 * поэтому новую роль добавляем в обоих местах.
 */

export const ROLE_IDS = ['CIVILIAN', 'MAFIA', 'DON', 'SHERIFF', 'DOCTOR', 'LOVER', 'MANIAC'] as const;
export type RoleId = (typeof ROLE_IDS)[number];

export type Team = 'TOWN' | 'MAFIA' | 'MANIAC';
export type Winner = Team | 'DRAW';

/** Ночные действия. Дон делает два действия: голосует с мафией и ищет шерифа. */
/** Порядок — как ведущий «будит» роли: мафия, дон, шериф, доктор, любовница. */
export const NIGHT_ACTIONS = ['MAFIA_KILL', 'DON_CHECK', 'SHERIFF_CHECK', 'HEAL', 'LOVER_VISIT', 'MANIAC_KILL'] as const;
export type NightActionKind = (typeof NIGHT_ACTIONS)[number];

export interface RoleDef {
  id: RoleId;
  team: Team;
  nightActions: readonly NightActionKind[];
  /** Сколько экземпляров роли допустимо в одной игре. */
  maxCount: number;
}

export const ROLES: Readonly<Record<RoleId, RoleDef>> = {
  CIVILIAN: { id: 'CIVILIAN', team: 'TOWN', nightActions: [], maxCount: Infinity },
  MAFIA: { id: 'MAFIA', team: 'MAFIA', nightActions: ['MAFIA_KILL'], maxCount: 5 },
  DON: { id: 'DON', team: 'MAFIA', nightActions: ['MAFIA_KILL', 'DON_CHECK'], maxCount: 1 },
  SHERIFF: { id: 'SHERIFF', team: 'TOWN', nightActions: ['SHERIFF_CHECK'], maxCount: 1 },
  DOCTOR: { id: 'DOCTOR', team: 'TOWN', nightActions: ['HEAL'], maxCount: 1 },
  LOVER: { id: 'LOVER', team: 'TOWN', nightActions: ['LOVER_VISIT'], maxCount: 1 },
  MANIAC: { id: 'MANIAC', team: 'MANIAC', nightActions: ['MANIAC_KILL'], maxCount: 1 },
};

export const teamOf = (role: RoleId): Team => ROLES[role].team;
export const isMafiaTeam = (role: RoleId): boolean => ROLES[role].team === 'MAFIA';

/** Отображаемые названия и описания (засеваются в таблицу Role, используются в UI). */
export const ROLE_INFO: Readonly<Record<RoleId, { name: string; description: string; emoji: string }>> = {
  CIVILIAN: {
    name: 'Мирный житель',
    emoji: '🧑‍🌾',
    description: 'Ночью спит. Днём ищет мафию и голосует, чтобы её изгнать.',
  },
  MAFIA: {
    name: 'Мафия',
    emoji: '🕶️',
    description: 'Знает сообщников. Ночью вместе с ними выбирает, кого убить. Днём притворяется мирным.',
  },
  DON: {
    name: 'Дон мафии',
    emoji: '🎩',
    description: 'Глава мафии: при равенстве голосов мафии решает он. Каждую ночь может проверить одного игрока — не шериф ли он.',
  },
  SHERIFF: {
    name: 'Шериф',
    emoji: '⭐',
    description: 'Каждую ночь проверяет одного игрока и узнаёт, мафия он или нет.',
  },
  DOCTOR: {
    name: 'Доктор',
    emoji: '💉',
    description: 'Каждую ночь лечит одного игрока и спасает его от убийства. Одного и того же — не две ночи подряд.',
  },
  LOVER: {
    name: 'Любовница',
    emoji: '🦋',
    description:
      'Ночная бабочка, играет за мирных. Ночью выбирает, с кем провести ночь: этот игрок днём молчит и не голосует. Если мафия придёт к нему — он выживет; если мафия придёт к ней — погибнут оба.',
  },
  MANIAC: {
    name: 'Маньяк',
    emoji: '🔪',
    description: 'Играет сам за себя: каждую ночь убивает одного игрока. Побеждает, если останется один на один.',
  },
};

export const TEAM_NAMES: Readonly<Record<Winner, string>> = {
  TOWN: 'Мирные жители',
  MAFIA: 'Мафия',
  MANIAC: 'Маньяк',
  DRAW: 'Ничья',
};
